import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type {
  UsageDays,
  UsageVersionCell,
  UsageVersionCompareRow,
  UsageVersionReport,
  UsageVersionRow,
} from '../types';
import {
  aenderungGesperrt,
  fensterText,
  formatDay,
  formatDuration,
  formatNumber,
  formatVitalValue,
  LEER,
  MIN_VERGLEICH_TAGE,
  richtung,
  stufenRichtung,
  stufenText,
  vitalRating,
} from '../services/usageFormat';
import { LCP_HIST_EDGES, VISIBLE_HIST_EDGES } from '../services/eimerLeitern';
import { auswahlNach, AuswahlFeld, StandAuswahl, vorgabeAuswahl } from '../services/versionsAuswahl';
import { useUsageVersionCompare } from '../hooks/useUsageVersionCompare';
import { SCHUTZ_OHNE_ZAHLEN } from './SchutzZeile';
import Abschnitt from './Abschnitt';
import Bewertungspunkt from './Bewertungspunkt';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';

/**
 * RITA-1523 (Nutzungserfassung) — Abschnitt „Versionsvergleich": zwei ausgelieferte Stände
 * nebeneinander, je Seite und als Gesamtzeile.
 *
 * Er steht direkt hinter „Seiten": die Vergleichszeilen sind eine Aufschlüsselung der Seitenzeilen
 * und werden in derselben Reihenfolge sortiert (Gesamtaufrufe über ALLE Stände, CONTRACT 3b V4).
 *
 * Eigener Abschnittsbaustein statt einer Tabelle in der NutzungPage (Muster FehlerAbschnitt), weil
 * er eine eigene Abfrage, eine eigene Auswahl und eigene Zustände hat: die beiden Kennungen folgen
 * erst aus der Ständeliste und wechseln ohne Zeitraumwechsel.
 *
 * Was hier NICHT passiert: rechnen. Jede Zahl kommt vom Server (readUsageDaily.ts). Die Oberfläche
 * entscheidet nur, was sie ÜBER die Zahlen sagt:
 *  - Auf Perzentilen steht eine Richtung mit Schwelle statt einer Differenz. Die Mediane sind
 *    Näherungen aus Eimern; ein ausgewiesenes „+4,2 s" könnte allein ein Eimerwechsel sein
 *    (services/eimerLeitern.ts, CONTRACT 3b V6).
 *  - Quoten kommen als STUFE (Feldname mit „Step", CONTRACT 3b V0a) und stehen deshalb als SPANNE
 *    in der Zelle („2 bis unter 5"); die Änderung zählt STUFEN, kein Δ. Beschriftet wird mit den
 *    Leitern AUS DER ANTWORT (`rateLadder`, `pagesPerSessionLadder`) — eine eigene Kopie wäre eine
 *    zweite Wahrheit. Der Grund steht in api/getUsageVersionCompare.ts: eine exakte Quote ließe
 *    sich mit den Aufrufen der Seiten-Tabelle zur Aufrufzahl je Stand zurückrechnen.
 *  - Exakt bleiben Mediane, Sitzungsdauer und Scrolltiefe: aus ihnen folgt kein Nenner.
 *  - Unter drei Tagen im Fenster bleibt die Änderungsspalte gesperrt.
 *  - Eine unterdrückte Zelle zeigt den Wortlaut von SchutzZeile, NIE Nullen; ein null-Wert einer
 *    bestehenden Zelle ist ein Strich („nicht gemessen", CONTRACT 5b).
 *  - Absolute Zahlen je Stand zeigt der Abschnitt nicht; die Antwort enthält keine. Die frühere
 *    Restunschärfe der Gesamtzeile (exakte Seiten je Sitzung gegen den Überblick) ist im BACKEND
 *    geschlossen, nicht hier: die Zahl kommt nur noch als Stufe.
 */
export interface VersionsvergleichAbschnittProps {
  days: UsageDays;
  /** Ständeliste aus dem Bündel-Dokument (Alias `versions`). */
  report: UsageVersionReport;
  /**
   * Lädt das Bündel neu, damit die Ständeliste wieder zum Fenster des Servers passt. Die Liste
   * gehört NICHT diesem Abschnitt — ohne diesen Rückruf könnte er eine abgelehnte Auswahl nur
   * gegen dieselbe veraltete Liste tauschen (siehe `selectionStale` unten).
   */
  onListeNeuLaden: () => void;
}

const ERLAEUTERUNG =
  'Ein Stand ist der Programmcode, der zu einem Zeitpunkt ausgeliefert war; die Kennung ist die ' +
  'Commit-Nummer aus GitLab, kein Release-Name. Nach einem Deploy arbeiten für einige Zeit beide ' +
  'Stände parallel — wer seinen Tab offen lässt, bleibt auf dem alten. Dieselbe Person kann deshalb ' +
  'in beiden Spalten stecken. Verglichen werden Mittelwerte und Quoten, keine absoluten Zahlen: ' +
  'absolute Zahlen je Stand ließen sich von den Seitenzahlen abziehen und beschrieben dann einzelne ' +
  'Personen. Angezeigt wird eine Seite nur, wenn dort mindestens fünf Personen auf dem jeweiligen ' +
  'Stand waren; bei rund 30 Nutzern sind das die meistbesuchten Seiten, kurz nach einem Deploy oft ' +
  'noch keine. Verweildauer und Ladeleistung sind Näherungen aus Messklassen — kleine Unterschiede ' +
  'sind kein Befund. Quoten erscheinen als Stufe, nicht als genaue Zahl — nicht wegen der Messung, ' +
  'sondern zum Schutz: eine genaue Quote ließe sich mit den Seitenaufrufen zur Zahl je Stand ' +
  'zurückrechnen.';

/** Zusatz am Schutz-Wortlaut: er benennt die Regel, ohne eine Zahl zu nennen. */
const SCHUTZ_ZUSATZ = 'weniger als 5 Personen auf diesem Stand';
const SCHUTZ_TEXT = `${SCHUTZ_OHNE_ZAHLEN} — ${SCHUTZ_ZUSATZ}`;

/** Text der Änderungsspalte, solange ein Stand zu kurz im Fenster liegt. */
const GESPERRT = 'gesperrt';
const GESPERRT_TITEL = `Änderung erst ab ${MIN_VERGLEICH_TAGE} Tagen eines Standes im Fenster bewertbar.`;

/** Farbe einer Richtung: kürzer und eine kleinere Quote sind die Verbesserung (Mockup v2, „gut"). */
const RICHTUNG_FARBE: Record<string, string> = {
  kürzer: 'success.main',
  länger: 'error.main',
  besser: 'success.main',
  schlechter: 'error.main',
};

/** Neutral: Gleichstand, Sperre und ein fehlender Wert sind keine Aussage über die Richtung. */
const RICHTUNG_NEUTRAL = 'text.disabled';

/**
 * Richtung → Farbe. Als eigene Funktion, weil die Farbe eine AUSSAGE ist: ein vertauschtes
 * grün/rot stellte eine Verbesserung als Verschlechterung dar, und die Zellprüfungen binden nur
 * `data-richtung`. Die Farbe steht nie allein — daneben steht das Wort (wie beim Bewertungspunkt,
 * apps/frontend/docs/HOTSPOTS.md).
 */
export const richtungFarbe = (wert: string): string => RICHTUNG_FARBE[wert] ?? RICHTUNG_NEUTRAL;

/**
 * Beschriftung eines Auswahleintrags. `usersEstimated` steht BEWUSST nicht daneben: `usersRounded`
 * ist mit Math.round auf ein Vielfaches von fünf gerundet (readUsageDaily.ts `roundUsers`) und
 * damit weder Ober- noch Untergrenze — ein „mindestens 25" wäre bei einer Untergrenze von 23 eine
 * falsche Zusage. Das „≥ N" aus `nutzerText` (SeitenTabelle) passt dort, weil es auf EXAKTEN Zahlen
 * sitzt. „ca." deckt hier beide Unschärfen ab: die Rundung und die gelöschten Pseudonymlisten.
 */
const beschriftung = (version: UsageVersionRow): string =>
  `${version.label} · neu ab ${formatDay(version.firstSeenDay)} · ca. ${version.usersRounded} Personen`;

/**
 * Satz zur Pseudonymfrist. Er steht im Hinweisfeld UND in der Fußzeile: reicht das Fenster über die
 * Frist hinaus, fehlen Stände, die nur in gelöschten Tagen liefen — ohne diesen Satz sähe das aus,
 * als habe es sie nie gegeben (usage.graphql, pseudonymWindowShort).
 */
const PSEUDONYM_FRIST =
  'Ältere Stände jenseits der Pseudonymfrist sind nicht mehr wählbar — dann hilft ein kürzeres ' +
  'Fenster nicht, sondern nur der Vergleich zu einem früheren Zeitpunkt.';

/** Hinweis über zu kleine Stände — dieselbe Formulierung in Fußzeile und Hinweisfeld. */
const staendeText = (hiddenVersions: number): string => {
  if (hiddenVersions <= 0) return '';
  return hiddenVersions === 1
    ? '1 weiterer Stand ist zu klein für eine Auswertung.'
    : `${hiddenVersions} weitere Stände sind zu klein für eine Auswertung.`;
};

const seitenText = (hiddenRoutes: number): string => {
  if (hiddenRoutes <= 0) return '';
  return hiddenRoutes === 1
    ? '1 Seite ist nicht vergleichbar, weil beide Stände dort zu wenige Personen haben.'
    : `${hiddenRoutes} Seiten sind nicht vergleichbar, weil beide Stände dort zu wenige Personen haben.`;
};

/** Auswahlfeld eines Standes: Kurzkennung, Erstauftritt und Größenordnung, Kennung im Tooltip. */
function StandFeld({
  feld,
  titel,
  wert,
  versionen,
  onWahl,
}: {
  feld: AuswahlFeld;
  titel: string;
  wert: string | null;
  versionen: readonly UsageVersionRow[];
  onWahl: (feld: AuswahlFeld, wert: string) => void;
}) {
  const kennung = `versionen-${feld}`;
  // Natives <select>: es ist mit Tastatur und Screenreader ohne Zusatzarbeit bedienbar, die
  // vollständige Kennung passt als `title` an Feld und Eintrag, und der Vertrag bleibt im Test
  // prüfbar. Ein Menü aus MenuItems brächte hier nur Schreibmaschinenschrift.
  return (
    <FormControl size="small" sx={{ minWidth: 300, flex: '1 1 300px' }}>
      <InputLabel shrink htmlFor={kennung}>
        {titel}
      </InputLabel>
      <Select
        native
        label={titel}
        value={wert ?? ''}
        onChange={(event) => onWahl(feld, String(event.target.value))}
        inputProps={{ id: kennung, 'data-testid': kennung, title: wert ?? '' }}
      >
        {/* Der leere Eintrag steht NUR, solange dieses Feld keine gültige Kennung trägt. Er erklärt
            den Zustand („kein zweiter Stand wählbar"); waehlbar sein darf er nicht, sonst liesse
            sich eine gültige Auswahl leeren — und ohne zwei Stände läuft keine Anfrage, die Seite
            stünde ohne Tabelle und ohne Erklärung da. */}
        {wert === null && (
          <option value="">{feld === 'vorher' ? 'kein zweiter Stand wählbar' : 'kein Stand wählbar'}</option>
        )}
        {versionen.map((version) => (
          <option key={version.appVersion} value={version.appVersion} title={version.appVersion}>
            {beschriftung(version)}
          </option>
        ))}
      </Select>
    </FormControl>
  );
}

/** Wert mit Bewertungspunkt (LCP, INP, CLS); ohne Messung ein Strich, nie eine Null. */
const vitalInhalt = (metric: 'LCP' | 'INP' | 'CLS', value: number | null): React.ReactNode => {
  if (value === null) return LEER;
  return (
    <>
      <Bewertungspunkt rating={vitalRating(metric, value)} />
      {formatVitalValue(metric, value)}
    </>
  );
};

interface ZellenProps {
  stand: 'A' | 'B';
  zelle: UsageVersionCell;
  /** Spalten, die die Schutz-Zelle überspannt. */
  spalten: number;
  inhalte: Array<{ name: string; inhalt: React.ReactNode }>;
}

/**
 * Die Spalten eines Standes — oder EINE Zelle mit dem Schutz-Wortlaut. Nullen an ihrer Stelle
 * behaupteten „nichts passiert", wo „nichts anzeigbar" gemeint ist (CONTRACT 5b).
 */
function StandZellen({ stand, zelle, spalten, inhalte }: ZellenProps) {
  if (zelle.suppressed) {
    return (
      <TableCell
        colSpan={spalten}
        data-zelle={`${stand}-schutz`}
        sx={{ color: 'warning.main', fontStyle: 'italic', borderLeft: 1, borderColor: 'divider' }}
      >
        {SCHUTZ_TEXT}
      </TableCell>
    );
  }
  return (
    <>
      {inhalte.map(({ name, inhalt }, stelle) => (
        <TableCell
          key={name}
          data-zelle={`${stand}-${name}`}
          sx={{ ...zahlenZelle, ...(stelle === 0 ? { borderLeft: 1, borderColor: 'divider' } : {}) }}
        >
          {inhalt}
        </TableCell>
      ))}
    </>
  );
}

/** Eine Zelle der Änderungsspalte: Text plus maschinenlesbare Richtung für Farbe und Tests. */
function AenderungZelle({
  name,
  wert,
  richtungswert,
  erste = false,
}: {
  name: string;
  wert: string;
  richtungswert: string;
  erste?: boolean;
}) {
  const farbe = richtungFarbe(richtungswert);
  return (
    <TableCell
      data-zelle={`delta-${name}`}
      data-richtung={richtungswert}
      title={richtungswert === GESPERRT ? GESPERRT_TITEL : undefined}
      sx={{
        ...zahlenZelle,
        ...(erste ? { borderLeft: 1, borderColor: 'divider' } : {}),
        color: farbe,
        fontWeight: farbe === RICHTUNG_NEUTRAL ? 400 : 600,
      }}
    >
      {wert}
    </TableCell>
  );
}

/** Pfeil vor der Richtung — dieselbe Aussage wie das Wort, nur schneller zu erfassen. */
const richtungText = (wert: string): string => {
  if (wert === 'kürzer') return '↓ kürzer';
  if (wert === 'länger') return '↑ länger';
  return wert;
};

interface AenderungProps {
  zeile: UsageVersionCompareRow;
  gesperrt: boolean;
  /** Kanten der Quotenstufen AUS DER ANTWORT — nur mit ihnen ist der Abstand in Stufen zählbar. */
  rateLadder: readonly number[];
}

/**
 * Die vier Änderungsspalten einer Zeile: zwei Richtungen mit Schwelle (Perzentile) und zwei
 * Stufenrichtungen (Quoten). Ein Δ gibt es auf keiner von beiden — auf den Perzentilen, weil der
 * Unterschied ein Eimerwechsel sein kann (V6), auf den Quoten, weil die Antwort keine exakte Quote
 * trägt und die Differenz zweier Kanten die Leiter beschriebe, nicht die Wirklichkeit (V0a).
 */
function AenderungZellen({ zeile, gesperrt, rateLadder }: AenderungProps) {
  const { standA, standB } = zeile;
  const verweildauer = richtung(standA.p50VisibleMs, standB.p50VisibleMs, VISIBLE_HIST_EDGES);
  const lcp = richtung(standA.lcpP50, standB.lcpP50, LCP_HIST_EDGES);
  const fehler = stufenRichtung(standA.errorRateStep, standB.errorRateStep, rateLadder);
  const frust = stufenRichtung(standA.frustRateStep, standB.frustRateStep, rateLadder);

  if (gesperrt) {
    return (
      <>
        {['verweildauer', 'lcp', 'fehler', 'frust'].map((name, stelle) => (
          <AenderungZelle key={name} name={name} wert={GESPERRT} richtungswert={GESPERRT} erste={stelle === 0} />
        ))}
      </>
    );
  }
  return (
    <>
      <AenderungZelle
        erste
        name="verweildauer"
        wert={verweildauer === null ? LEER : richtungText(verweildauer)}
        richtungswert={verweildauer ?? 'fehlt'}
      />
      <AenderungZelle name="lcp" wert={lcp === null ? LEER : richtungText(lcp)} richtungswert={lcp ?? 'fehlt'} />
      <AenderungZelle
        name="fehler"
        wert={fehler === null ? LEER : fehler.text}
        richtungswert={fehler?.bewertung ?? 'fehlt'}
      />
      <AenderungZelle
        name="frust"
        wert={frust === null ? LEER : frust.text}
        richtungswert={frust?.bewertung ?? 'fehlt'}
      />
    </>
  );
}

/**
 * Unterzeile der Gesamtzeile: Seiten je Sitzung als STUFE, Sitzungsdauer exakt. Die Einheit steht
 * einmal am ersten Stand — zweimal „Seiten je Sitzung" in einer Unterzeile wäre Rauschen.
 */
const gesamtUnterzeile = (zeile: UsageVersionCompareRow, pagesLadder: readonly number[]): string => {
  const { standA, standB } = zeile;
  const seiten = (zelle: UsageVersionCell): string => stufenText(zelle.pagesPerSessionStep, pagesLadder, 1);
  return (
    `Vorher ${seiten(standA)} Seiten je Sitzung · ${formatDuration(standA.avgSessionMs)}` +
    ` · Nachher ${seiten(standB)} · ${formatDuration(standB.avgSessionMs)}`
  );
};

/** Die vier Hauptkennzahlen eines Standes; die Quoten als Spanne ihrer Stufe. */
const hauptInhalte = (zelle: UsageVersionCell, rateLadder: readonly number[]) => [
  { name: 'verweildauer', inhalt: formatDuration(zelle.p50VisibleMs) },
  { name: 'lcp', inhalt: vitalInhalt('LCP', zelle.lcpP50) },
  { name: 'fehler', inhalt: stufenText(zelle.errorRateStep, rateLadder) },
  { name: 'frust', inhalt: stufenText(zelle.frustRateStep, rateLadder) },
];

/** Die vier weiteren Kennzahlen eines Standes (zweite Tabelle); Scrolltiefe bleibt exakt. */
const weitereInhalte = (zelle: UsageVersionCell, rateLadder: readonly number[]) => [
  { name: 'inp', inhalt: vitalInhalt('INP', zelle.inpP50) },
  { name: 'cls', inhalt: vitalInhalt('CLS', zelle.clsP50) },
  { name: 'ruecksprung', inhalt: stufenText(zelle.bounceRateStep, rateLadder) },
  { name: 'scroll', inhalt: zelle.avgScrollPct === null ? LEER : `${formatNumber(zelle.avgScrollPct, 0)} %` },
];

export default function VersionsvergleichAbschnitt({ days, report, onListeNeuLaden }: VersionsvergleichAbschnittProps) {
  const versionen = report.versions;
  const vorgabe = useMemo(() => vorgabeAuswahl(versionen), [versionen]);
  const [auswahl, setAuswahl] = useState<StandAuswahl | null>(null);
  const [weitereOffen, setWeitereOffen] = useState(false);
  // Die Anzeige prüft die eigene Auswahl gegen die LISTE, nicht erst der Effekt unten: zwischen
  // Ankunft einer frischen Liste und dem Zurücksetzen liegt ein Render, und ein <select> mit einem
  // Wert ohne <option> stünde leer da.
  const bekannt = (appVersion: string | null) =>
    appVersion !== null && versionen.some((version) => version.appVersion === appVersion);
  const gewaehlt = auswahl !== null && bekannt(auswahl.vorher) && bekannt(auswahl.nachher) ? auswahl : vorgabe;

  const vergleich = useUsageVersionCompare(days, gewaehlt.vorher, gewaehlt.nachher);
  const daten = vergleich.data?.usageVersionCompare;
  const stale = Boolean(daten?.selectionStale);

  // Eine nicht mehr wählbare Auswahl ist kein Fehler: ein Verdichtungslauf zwischen zwei Anfragen
  // verschiebt das Fenster und damit die Liste (CONTRACT 3b V8). Bei JEDER solchen Antwort wird die
  // Vorgabe des aktuellen Fensters neu gesetzt — Abhängigkeit ist deshalb die Antwort selbst, nicht
  // die Marke: eine zweite stale Antwort zu einer inzwischen anderen Auswahl muss wieder greifen.
  //
  // Das Zurücksetzen allein genügt NICHT. Ist die Vorgabe selbst das abgelehnte Paar — der Regelfall,
  // wenn die Verdichtung den jüngsten Stand aus dem Fenster schiebt —, ändert es nichts, es läuft
  // keine neue Anfrage, und der Abschnitt bliebe ohne Tabelle im Hinweis stehen. Deshalb wird die
  // LISTE neu geladen; sie gehört dem Bündel, nicht diesem Abschnitt.
  //
  // Die Sperre merkt sich die bereits nachgeladenen Paare (die des BERICHTS, nicht die inzwischen
  // gewählten): jedes abgelehnte Paar löst genau ein Neuladen aus. Ohne sie liefe ein Kreislauf,
  // sobald der Server dasselbe Paar erneut ablehnt.
  const nachgeladen = useRef(new Set<string>());
  useEffect(() => {
    if (daten === undefined || !daten.selectionStale) return;
    const abgelehnt = `${daten.versionA}|${daten.versionB}`;
    if (!nachgeladen.current.has(abgelehnt)) {
      nachgeladen.current.add(abgelehnt);
      onListeNeuLaden();
    }
    setAuswahl((alt) =>
      alt !== null && alt.vorher === vorgabe.vorher && alt.nachher === vorgabe.nachher ? alt : vorgabe,
    );
  }, [daten, vorgabe, onListeNeuLaden]);

  const waehle = (feld: AuswahlFeld, wert: string) => setAuswahl(auswahlNach(versionen, gewaehlt, feld, wert));

  const zuWenige = versionen.length < 2;
  const gesperrt = daten !== undefined && aenderungGesperrt(daten.total.standA.days, daten.total.standB.days);
  // Die Leitern gehören zur ANTWORT (CONTRACT 3b V0a); ohne Antwort wird auch keine Zeile gebaut.
  // Eine leere Leiter lässt die Quotenzellen leer (`stufenText`) — nie eine exakte Quote.
  const rateLadder = daten?.rateLadder ?? [];
  const pagesLadder = daten?.pagesPerSessionLadder ?? [];
  const labelA = versionen.find((version) => version.appVersion === gewaehlt.vorher)?.label ?? LEER;
  const labelB = versionen.find((version) => version.appVersion === gewaehlt.nachher)?.label ?? LEER;

  /** Welcher Stand die Sperre auslöst — mit seinen Tagen, damit der Hinweis überprüfbar ist. */
  const sperrHinweis = (): string => {
    if (daten === undefined) return '';
    const kurz = [
      { label: labelA, days: daten.total.standA.days },
      { label: labelB, days: daten.total.standB.days },
    ].filter((stand) => stand.days === null || stand.days < MIN_VERGLEICH_TAGE);
    if (kurz.length === 0) return '';
    if (kurz.some((stand) => stand.days === null)) {
      return `Änderung noch nicht bewertbar: für mindestens einen Stand liegen weniger als ${MIN_VERGLEICH_TAGE} Tage im Fenster.`;
    }
    const teile = kurz.map(
      (stand) => `${stand.label} liegt erst ${stand.days} ${stand.days === 1 ? 'Tag' : 'Tage'} im Fenster`,
    );
    return `Änderung noch nicht bewertbar: der Stand ${teile.join(', der Stand ')} (ab ${MIN_VERGLEICH_TAGE} Tagen).`;
  };

  const zeileMarkup = (zeile: UsageVersionCompareRow, gesamt: boolean, schluessel: string) => (
    <TableRow
      key={schluessel}
      data-testid={gesamt ? 'versionen-gesamt' : undefined}
      sx={gesamt ? { '& td': { bgcolor: 'action.hover', fontWeight: 600 } } : undefined}
    >
      <TableCell>
        <Typography
          component="span"
          variant="body2"
          sx={{ fontFamily: gesamt ? undefined : 'Menlo, Consolas, monospace', wordBreak: 'break-word' }}
        >
          {zeile.route}
        </Typography>
        {gesamt && (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontWeight: 400 }}
          >
            {gesamtUnterzeile(zeile, pagesLadder)}
          </Typography>
        )}
      </TableCell>
      <StandZellen stand="A" zelle={zeile.standA} spalten={4} inhalte={hauptInhalte(zeile.standA, rateLadder)} />
      <StandZellen stand="B" zelle={zeile.standB} spalten={4} inhalte={hauptInhalte(zeile.standB, rateLadder)} />
      <AenderungZellen zeile={zeile} gesperrt={gesperrt} rateLadder={rateLadder} />
    </TableRow>
  );

  const weitereZeile = (zeile: UsageVersionCompareRow, gesamt: boolean, schluessel: string) => (
    <TableRow key={schluessel} sx={gesamt ? { '& td': { bgcolor: 'action.hover', fontWeight: 600 } } : undefined}>
      <TableCell>{zeile.route}</TableCell>
      <StandZellen stand="A" zelle={zeile.standA} spalten={4} inhalte={weitereInhalte(zeile.standA, rateLadder)} />
      <StandZellen stand="B" zelle={zeile.standB} spalten={4} inhalte={weitereInhalte(zeile.standB, rateLadder)} />
    </TableRow>
  );

  return (
    <Abschnitt titel="Versionsvergleich" testId="nutzung-versionen" erlaeuterung={ERLAEUTERUNG}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, maxWidth: 780 }}>
        <StandFeld feld="vorher" titel="Vorher" wert={gewaehlt.vorher} versionen={versionen} onWahl={waehle} />
        <StandFeld feld="nachher" titel="Nachher" wert={gewaehlt.nachher} versionen={versionen} onWahl={waehle} />
      </Box>

      {zuWenige && (
        <Alert severity="info" data-testid="versionen-hinweis-wenige">
          {versionen.length === 1
            ? 'Nur ein Stand hat im Zeitraum genug Personen für eine Auswertung. Ein Vergleich braucht zwei Stände mit je mindestens fünf Personen.'
            : 'Im Zeitraum ist kein Stand auswertbar. Ein Vergleich braucht zwei Stände mit je mindestens fünf Personen.'}
          {staendeText(report.hiddenVersions) !== '' && ` ${staendeText(report.hiddenVersions)}`}
          {report.pseudonymWindowShort && ` ${PSEUDONYM_FRIST}`}
        </Alert>
      )}

      {!zuWenige && stale && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="versionen-hinweis-stale">
          Die gewählte Auswahl ist im Zeitraum nicht mehr gültig — die Liste wird neu geladen und die Vorgabe neu
          gesetzt. Das passiert auch ohne Zutun: die nächtliche Verdichtung verschiebt den Zeitraum.
        </Alert>
      )}

      {!zuWenige && !stale && gesperrt && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="versionen-hinweis-sperre">
          {sperrHinweis()}
        </Alert>
      )}

      {!zuWenige && vergleich.loading && (
        <Box data-testid="versionen-ladend">
          <Skeleton width={320} />
          <Skeleton variant="rectangular" height={160} sx={{ mt: 1, borderRadius: 2 }} />
        </Box>
      )}

      {!zuWenige && !vergleich.loading && vergleich.error && (
        <Alert
          severity="error"
          data-testid="versionen-fehler"
          action={
            <Button
              color="inherit"
              size="small"
              data-testid="versionen-erneut"
              onClick={() => {
                vergleich.refetch().catch(() => undefined);
              }}
            >
              Erneut versuchen
            </Button>
          }
        >
          <AlertTitle>Der Versionsvergleich konnte nicht geladen werden.</AlertTitle>
          {vergleich.error.message}
        </Alert>
      )}

      {!zuWenige && !vergleich.loading && !vergleich.error && daten && !stale && (
        <>
          <Box data-testid="versionen-tabelle">
            <DatenTabelle
              leer={false}
              leerText=""
              kopfgruppen={[
                { titel: '', spalten: 1 },
                { titel: `Vorher · ${labelA}`, spalten: 4 },
                { titel: `Nachher · ${labelB}`, spalten: 4 },
                { titel: 'Änderung (Nachher gegen Vorher)', spalten: 4 },
              ]}
              spalten={[
                { titel: 'Seite' },
                { titel: 'Verweildauer (Median)', numerisch: true },
                { titel: 'LCP (Median)', numerisch: true },
                // Die Einheit gehört in den Kopf: in der Zelle steht die SPANNE der Stufe, und
                // „0,5 bis unter 1" ohne Bezug läse sich wie ein Anteil von Personen.
                { titel: 'Fehler (Stufe je 100 Aufrufe)', numerisch: true },
                { titel: 'Frustklicks (Stufe je 100 Aufrufe)', numerisch: true },
                { titel: 'Verweildauer (Median)', numerisch: true },
                { titel: 'LCP (Median)', numerisch: true },
                { titel: 'Fehler (Stufe je 100 Aufrufe)', numerisch: true },
                { titel: 'Frustklicks (Stufe je 100 Aufrufe)', numerisch: true },
                { titel: 'Verweildauer', numerisch: true },
                { titel: 'LCP', numerisch: true },
                { titel: 'Fehler', numerisch: true },
                { titel: 'Frustklicks', numerisch: true },
              ]}
            >
              {zeileMarkup(daten.total, true, 'gesamt')}
              {daten.rows.map((zeile, stelle) =>
                // Die Reihenfolge des Servers ist fest (Gesamtaufrufe über alle Stände); die Stelle
                // ist deshalb der stabile Schlüssel (wie SeitenTabelle).
                // eslint-disable-next-line react/no-array-index-key
                zeileMarkup(zeile, false, `${zeile.route}-${stelle}`),
              )}
            </DatenTabelle>
          </Box>

          <Button
            size="small"
            data-testid="versionen-weitere-schalter"
            aria-expanded={weitereOffen}
            onClick={() => setWeitereOffen((offen) => !offen)}
            endIcon={weitereOffen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mt: 1 }}
          >
            Weitere Kennzahlen je Stand
          </Button>
          <Collapse in={weitereOffen} unmountOnExit>
            <Box data-testid="versionen-weitere" sx={{ mt: 1 }}>
              <DatenTabelle
                leer={false}
                leerText=""
                kopfgruppen={[
                  { titel: '', spalten: 1 },
                  { titel: `Vorher · ${labelA}`, spalten: 4 },
                  { titel: `Nachher · ${labelB}`, spalten: 4 },
                ]}
                spalten={[
                  { titel: 'Seite' },
                  { titel: 'INP (Median)', numerisch: true },
                  { titel: 'CLS (Median)', numerisch: true },
                  // Rücksprünge sind eine Quote mit kleinem ganzzahligem Zähler — also eine Stufe.
                  // Die Scrolltiefe ist ein Mittelwert über eine große Summe und bleibt exakt.
                  { titel: 'Rücksprünge (Stufe je 100 Aufrufe)', numerisch: true },
                  { titel: 'Ø Scrolltiefe', numerisch: true },
                  { titel: 'INP (Median)', numerisch: true },
                  { titel: 'CLS (Median)', numerisch: true },
                  { titel: 'Rücksprünge (Stufe je 100 Aufrufe)', numerisch: true },
                  { titel: 'Ø Scrolltiefe', numerisch: true },
                ]}
              >
                {weitereZeile(daten.total, true, 'gesamt')}
                {daten.rows.map((zeile, stelle) =>
                  // eslint-disable-next-line react/no-array-index-key
                  weitereZeile(zeile, false, `${zeile.route}-${stelle}`),
                )}
              </DatenTabelle>
            </Box>
          </Collapse>
        </>
      )}

      {!zuWenige && !vergleich.loading && !vergleich.error && daten && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          data-testid="versionen-fuss"
          sx={{ mt: 1.5, display: 'grid', gap: 0.5 }}
        >
          {seitenText(daten.hiddenRoutes) !== '' && <span>{seitenText(daten.hiddenRoutes)}</span>}
          {staendeText(report.hiddenVersions) !== '' && <span>{staendeText(report.hiddenVersions)}</span>}
          {report.pseudonymWindowShort && <span>{PSEUDONYM_FRIST}</span>}
          <span>{`Stand: ${formatDay(daten.dataUntil)} (Fenster ${fensterText(daten.dataFrom, daten.dataUntil)})`}</span>
        </Typography>
      )}
    </Abschnitt>
  );
}
